import React, { useState, useRef, useEffect } from 'react';
import { Menu } from 'lucide-react';
import favicon from '../assets/images/favicon.png';
import Schedule from './schedule';
import Patients from './patient/patients';
import PatientHome from './patient/PatientHome';
import BillingHome from './billing/BillingHome';
import AdminHome from './admin/AdminHome';
import Sidebar from './layout/Sidebar';
import { fetchMyPatients } from '../services/admin/patient.service';
import { profileImageService } from '../services/profile/profileImage.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authcontext';
import { usePatient } from '../context/patientContext';
import SetupChecklist from './admin/SetupChecklist';
import type { SetupItemKey } from '../dtos/admin/setupStatus.dto';
import { OPEN_ADMIN_TAB_EVENT, OPEN_PATIENT_CHART_EVENT, OPEN_SECTION_EVENT, ORGANIZATION_ACCOUNT_TAB } from '../constants/navigationEvents';
import CapUsageBanner from './subscription/CapUsageBanner';
import { useBillingReturn } from '../hooks/useBillingReturn';
import MyDay from './visit/MyDay';
import ReviewQueuePage from './review/ReviewQueuePage';
import { visitWorkspacePath } from './offline/workspace/VisitWorkspaceRoute';
import ActiveVisitBar from './visit/ActiveVisitBar';
import ClinicianBottomNav from './layout/ClinicianBottomNav';
import type { StartVisitShiftInfo } from './visit/StartVisitSheet';
import { fetchPatientById } from '../services/admin/patient.service';
import { usePermission } from '../hooks/usePermission';
import { createPermissionString as perm } from '../types/permissions';
import { useFieldMode } from '../hooks/useOfflineDevice';
import SandboxChip from './demo/SandboxChip';
import { NotificationProvider } from '../context/NotificationContext';
import NotificationBell from './notifications/NotificationBell';

/** Admin tab that resolves each setup checklist item (Phase 5, ACC-21) */
const SETUP_ITEM_TABS: Record<SetupItemKey, string> = {
  agencyProfile: 'agencySetup',
  evvChecklist: 'agencySetup',
  staffUser: 'userMgmt',
  patient: 'patientAdmin',
};

interface DashboardProps {
  onLogout?: () => void | Promise<void>;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { patientUnselected, setPatientUnselected, setSelectedPatient: setContextPatient } = usePatient();
  // Phase 9B: clinicians (start visits, no all-patient schedule) land on My Day
  const canStartVisits = usePermission(perm('patientmr:phi:visit:create'));
  const canReadAllSchedule = usePermission(perm('scheduling:phi:readall'));
  const isClinician = canStartVisits && !canReadAllSchedule;

  // UI States
  // Phase 16A: clinicians on a touch phone or tablet get the touch layout at any width
  const fieldMode = useFieldMode(isClinician);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 768);
  const isMobile = isNarrow || fieldMode;
  // Desktop: false = pinned (sidebar pushes content), true = unpinned (sidebar hidden, hover-to-show)
  // Mobile: false = open, true = closed
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  // sidebarPinned tracks whether the desktop sidebar is pinned open (pushing content)
  // Defaults to true so existing desktop behavior is preserved until user unpins
  const [sidebarPinned, setSidebarPinned] = useState<boolean>(
    () => localStorage.getItem('sidebar_pinned') !== 'false'
  );
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [myPatientsExpanded, setMyPatientsExpanded] = useState(false);
  
  // Navigation States
  const [activeSection, setActiveSection] = useState('Patients');
  const [activeSubsection, setActiveSubsection] = useState(null);
  const [activeAdminTab, setActiveAdminTab] = useState(null);
  const [activeSidebarSection, setActiveSidebarSection] = useState('Charting');
  const [expandedSections, setExpandedSections] = useState({
    'Patients': false,
    'Schedule': false,
    'Billing': false,
    'Administration': false,
    'Charting': true,
    'Medications': false,
    'Care Plan': false,
    'Demographics': false,
    'Assessments': false,
    'Growth Tracking': false,
    'Authorizations': false,
    'Care Team': false
  });

  // Patient Admin Form Navigation State
  const [patientAdminMode, setPatientAdminMode] = useState<'list' | 'add' | 'edit'>('list');
  const [patientEditId, setPatientEditId] = useState<string | null>(null);
  
  // Data States
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [pendingStart, setPendingStart] = useState<{ patientId: string; shift: StartVisitShiftInfo } | null>(null);
  // Phase 18U U16: open this visit's chart for review once the patient chart is open
  const [pendingReview, setPendingReview] = useState<{ patientId: string; visitId: string } | null>(null);
  const landedRef = useRef(false);
  const [apiPatients, setApiPatients] = useState([]);
  const [apiPatientsLoading, setApiPatientsLoading] = useState(true);
  const [userProfilePic, setUserProfilePic] = useState(null);

  // Refs
  const patientSearchInputRef = useRef(null);
  const patientSearchResultsRef = useRef(null);
  const patientsSectionRef = useRef(null);

  // SignOut: App's logout revokes tokens (GlobalSignOut) and clears them from memory.
  // The single idle timer (useSessionTimeout) lives in App.tsx.
  const signOut = React.useCallback(async () => {
    await onLogout?.();
    navigate('/login', { replace: true });
  }, [navigate, onLogout]);

  // Effects
  useEffect(() => {
    if (!user) {
      signOut();
    }
  }, [user, signOut]);

  // Fetch profile picture when user logs in
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (user) {
        try {
          const profileData = await profileImageService.getProfileImage();
          if (profileData.hasProfilePicture && profileData.imageUrl) {
            setUserProfilePic(profileData.imageUrl);
          } else {
            setUserProfilePic(null);
          }
        } catch (error) {
          setUserProfilePic(null);
        }
      }
    };

    fetchProfilePicture();
  }, [user]);

  // Other screens (e.g. a PA form PDF blocked by an incomplete agency profile) open an admin tab
  useEffect(() => {
    const handleOpenAdminTab = (event: Event) => {
      const tab = (event as CustomEvent<string>).detail;
      if (typeof tab !== 'string' || !tab) return;
      setActiveSection('Administration');
      setActiveAdminTab(tab);
    };
    window.addEventListener(OPEN_ADMIN_TAB_EVENT, handleOpenAdminTab);
    return () => window.removeEventListener(OPEN_ADMIN_TAB_EVENT, handleOpenAdminTab);
  }, []);

  // Auto-expand panels when activeSubsection changes (for mobile navigation)
  useEffect(() => {
    if (activeSubsection && activeSubsection !== 'Overview') {
      setExpandedSections(prev => ({
        ...prev,
        [activeSubsection]: true
      }));
    }
  }, [activeSubsection]);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsNarrow(window.innerWidth < 768);
      // Sidebar state is preserved when switching between mobile and desktop
      // The sidebar maintains its expanded/collapsed state regardless of screen size
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // A tablet in field mode starts with the sidebar closed, like a phone
  useEffect(() => {
    if (fieldMode) setSidebarCollapsed(true);
  }, [fieldMode]);

  useEffect(() => {
    if (activeSubsection) {
      setActiveSidebarSection(activeSubsection);
    }
  }, [activeSubsection]);

  useEffect(() => {
    if (patientUnselected) {
      setSelectedPatient(null);
      if (activeSection === 'Patients') {
        setActiveSubsection(null);
      }
      setPatientUnselected(false);
    }
  }, [patientUnselected, activeSection, setPatientUnselected]);

  useEffect(() => {
    if (user) {
      setApiPatientsLoading(true);
      fetchMyPatients()
        .then(patients => {
          const formattedPatients = patients.map(patient => ({
            id: patient.id,
            firstName: patient.firstName,
            lastName: patient.lastName,
            name: `${patient.firstName} ${patient.lastName}`,
            dob: patient.dob,
            mrn: patient.mrn,
            gender: patient.gender,
            status: patient.status === 'ACTIVE' ? 'Active' : patient.status === 'ON_HOLD' ? 'On Hold' : 'Inactive',
            initials: `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`
          }));
          setApiPatients(formattedPatients);
          setApiPatientsLoading(false);
        })
        .catch(error => {
          setApiPatients([]);
          setApiPatientsLoading(false);
        });
    } else {
      setApiPatients([]);
      setApiPatientsLoading(false);
    }
  }, [user]);

  // First landing: clinicians start on My Day (once, after permissions load)
  useEffect(() => {
    if (landedRef.current || !user?.permissions?.length) return;
    landedRef.current = true;
    if (isClinician && activeSection === 'Patients' && !activeSubsection) setActiveSection('MyDay');
  }, [user?.permissions, isClinician, activeSection, activeSubsection]);

  /** Open a patient chart by id (My Day, the active-visit bar); optionally start a shift there. */
  const openPatientChart = async (patientId: string, tab: string = 'Overview', shift?: StartVisitShiftInfo, reviewVisitId?: string) => {
    try {
      const patient = await fetchPatientById(patientId);
      setSelectedPatient(patient);
      setContextPatient(patient);
      setActiveSection('Patients');
      setActiveSubsection(tab);
      if (shift) setPendingStart({ patientId, shift });
      if (reviewVisitId) setPendingReview({ patientId, visitId: reviewVisitId });
    } catch {
      // The chart could not be loaded; stay where we are
    }
  };

  // Phase 13: open a chart by ID from elsewhere (e.g. after loading the sample patient)
  const openPatientChartRef = useRef(openPatientChart);
  openPatientChartRef.current = openPatientChart;
  useEffect(() => {
    const handleOpenPatientChart = (event: Event) => {
      const detail = (event as CustomEvent<{ patientId?: string; tab?: string; visitId?: string }>).detail;
      const patientId = detail?.patientId;
      // Phase 16F: notification deep links name the chart tab
      const tab = typeof detail?.tab === 'string' && detail.tab ? detail.tab : undefined;
      // Phase 18U U16: the review queue names the visit to open
      const visitId = typeof detail?.visitId === 'string' && detail.visitId ? detail.visitId : undefined;
      if (typeof patientId === 'string' && patientId) openPatientChartRef.current(patientId, tab, undefined, visitId);
    };
    window.addEventListener(OPEN_PATIENT_CHART_EVENT, handleOpenPatientChart);
    return () => window.removeEventListener(OPEN_PATIENT_CHART_EVENT, handleOpenPatientChart);
  }, []);

  // Phase 16F: notification deep links open the schedule (Today / Week) or a billing tab
  useEffect(() => {
    const handleOpenSection = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: string; subsection?: string }>).detail;
      if (detail?.section !== 'Schedule' && detail?.section !== 'Billing') return;
      setActiveSection(detail.section);
      setActiveSubsection(detail.subsection || null);
    };
    window.addEventListener(OPEN_SECTION_EVENT, handleOpenSection);
    return () => window.removeEventListener(OPEN_SECTION_EVENT, handleOpenSection);
  }, []);

  // Phase 13: back from Stripe Checkout / customer portal (?billing=…) → Organization Account
  useBillingReturn(() => {
    setActiveSection('Administration');
    setActiveAdminTab(ORGANIZATION_ACCOUNT_TAB);
  });

  // Handlers
  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleSidebarPin = () => {
    setSidebarPinned(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_pinned', String(next));
      return next;
    });
  };
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleMainContentClick = () => {
    if (isMobile && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  };
  const scrollToSection = (section) => {
    setActiveSection('Patients');
    setActiveSubsection(section);
    
    // Automatically expand the corresponding panel when navigating to a section
    if (section && section !== 'Overview') {
      setExpandedSections(prev => ({
        ...prev,
        [section]: true
      }));
    }
  };
  // Phone bottom nav for clinicians outside a patient chart (the chart has its own)
  const showClinicianNav = isMobile && isClinician && sidebarCollapsed && !(activeSection === 'Patients' && activeSubsection);
  const handlePatientSearch = (e) => setPatientSearchQuery(e.target.value);
  const handleSignOut = async () => await signOut();
  
  // Handle profile picture update
  const handleProfilePictureUpdate = (newImageUrl: string) => {
    setUserProfilePic(newImageUrl);
  };

  return (
    <NotificationProvider>
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar - Full height */}
      <Sidebar
        isMobile={isMobile}
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={toggleSidebar}
        sidebarPinned={sidebarPinned}
        toggleSidebarPin={toggleSidebarPin}
        sidebarHovered={sidebarHovered}
        setSidebarHovered={setSidebarHovered}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        myPatientsExpanded={myPatientsExpanded}
        setMyPatientsExpanded={setMyPatientsExpanded}
        myPatients={apiPatients}
        myPatientsLoading={apiPatientsLoading}
        activeSection={activeSection}
        activeSubsection={activeSubsection}
        activeAdminTab={activeAdminTab}
        activeSidebarSection={activeSidebarSection}
        setActiveSection={setActiveSection}
        setActiveSubsection={setActiveSubsection}
        setActiveAdminTab={setActiveAdminTab}
        scrollToSection={scrollToSection}
        handleSignOut={handleSignOut}
        userId={user?.id || ''}
        userProfilePic={userProfilePic}
        onProfilePictureUpdate={handleProfilePictureUpdate}
        patientSearchQuery={patientSearchQuery}
        setPatientSearchQuery={setPatientSearchQuery}
        handlePatientSearch={handlePatientSearch}
        patientSearchInputRef={patientSearchInputRef}
        patientSearchResultsRef={patientSearchResultsRef}
        firstName={user?.firstName || ''}
        lastName={user?.lastName || ''}
        currentUserEmail={user?.email || ''}
      />

      {/* Main Content Area */}
      <div
        className="flex-1 flex flex-col min-h-0 h-screen overflow-auto"
      >
        {/* Header - Only shows when sidebar is collapsed on mobile, or always on desktop */}
        {(sidebarCollapsed || !isMobile) && (
          <header className={`bg-white border-b border-gray-200 flex items-center justify-between z-30 flex-shrink-0 ${isMobile ? 'h-16 px-2 py-2' : 'h-[58px] px-4 py-4'}`} style={{ position: 'sticky', top: 0, WebkitPosition: 'sticky' } as React.CSSProperties}>
            <div className="flex items-center">
              {/* Mobile burger */}
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  className="p-2 text-slate-800 hover:text-slate-600 mr-1.5"
                  aria-label="Open menu"
                >
                  <Menu size={26} />
                </button>
              )}
              {/* Desktop burger — only shown when sidebar is unpinned */}
              {!isMobile && !sidebarPinned && (
                <button
                  onClick={() => setSidebarHovered(true)}
                  className="p-2 text-slate-700 hover:text-slate-900 mr-1.5"
                  aria-label="Open sidebar"
                  title="Open sidebar"
                >
                  <Menu size={22} />
                </button>
              )}
              
              {(sidebarCollapsed || !isMobile) && (
                <div className="flex items-center">
                  <img src={favicon} alt="Pelora Health" className="h-6 w-6 mr-1" />
                  <span className="text-slate-900 text-lg font-semibold">Pelora Health</span>
                </div>
              )}
            </div>

            <div className={`flex-1`} />

            <div className={`flex items-center space-x-1.5 ${isMobile && !sidebarCollapsed ? 'hidden' : 'flex'}`}>
              {/* Demo sandbox: days left and the persona switcher (renders nothing for a normal tenant) */}
              <SandboxChip />
              {/* Phase 16F: critical notifications (renders nothing without notifications:self:read) */}
              <NotificationBell />
            </div>
          </header>
        )}

        {canStartVisits && (
          <ActiveVisitBar
            onOpen={(patientId) => openPatientChart(patientId, 'Charting')}
            hiddenForPatientId={activeSection === 'Patients' && activeSubsection ? selectedPatient?.id : null}
          />
        )}

        {/* Guided setup checklist (owner/admin); replaces the client-side EVV banner */}
        <SetupChecklist
          className="rounded-none border-x-0 border-t-0"
          onNavigate={(key, item) => {
            // Drive navigation from the backend's section/adminTab/anchor; fall back to the key map
            setActiveSection(item.section || 'Administration');
            setActiveAdminTab(item.adminTab || SETUP_ITEM_TABS[key]);
            const anchor = item.anchor ?? (key === 'evvChecklist' ? 'evv' : null);
            if (anchor) {
              // Wait for the tab to render, then scroll to the anchor
              window.setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
            }
          }}
        />

        {/* Phase 13 patient cap notice (≥80%); the patient list shows its own, charts show none */}
        {activeSection !== 'Patients' && <CapUsageBanner className="rounded-none border-x-0 border-t-0" />}

        {/* Main Content */}
        <main className={`flex-1 min-h-0 ${showClinicianNav ? 'pb-20' : ''}`} onClick={handleMainContentClick}>
            {activeSection === 'MyDay' ? (
              <MyDay
                onOpenChart={(patientId) => openPatientChart(patientId)}
                onStartShift={(patientId, shift) => openPatientChart(patientId, 'Overview', shift)}
                onOpenVisit={(patientId, visitId) => navigate(visitWorkspacePath(visitId, patientId))}
              />
            ) : activeSection === 'Schedule' ? (
              <Schedule subsection={activeSubsection} />
            ) : activeSection === 'Patients' ? (
              <>
                {!activeSubsection ? (
                  <PatientHome 
                      currentUserEmail={user?.email}
                      selectedPatientId={selectedPatient?.id}
                      onSelectPatient={(patient) => {
                        setSelectedPatient(patient);
                        if (patient) {
                          toggleSection('Patients');
                          setActiveSubsection('Charting');
                        } else {
                          setActiveSubsection(null);
                        }
                      }}
                      onShowPatientChart={(patient) => {
                        // Only set the patient if it's different from the currently selected patient
                        if (selectedPatient?.id !== patient.id) {
                          setSelectedPatient(patient);
                        }
                        toggleSection('Patients');
                        setActiveSubsection('Overview');
                      }}
                    />
                ) : (
                  <Patients
                    ref={patientsSectionRef}
                    patientsExpanded={expandedSections['Patients']}
                    togglePatients={() => toggleSection('Patients')}
                    sidebarCollapsed={sidebarCollapsed}
                    activeSubsection={activeSubsection}
                    selectedPatient={selectedPatient}
                    onSidebarSelectionChange={(section) => setActiveSidebarSection(section)}
                    setActiveSubsection={setActiveSubsection}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    pendingStart={pendingStart}
                    onPendingStartHandled={() => setPendingStart(null)}
                    pendingReview={pendingReview}
                    onPendingReviewHandled={() => setPendingReview(null)}
                  />
                )}
              </>
            ) : activeSection === 'Administration' ? (
              <AdminHome
                activeAdminTab={activeAdminTab}
                setActiveAdminTab={setActiveAdminTab}
                sidebarCollapsed={sidebarCollapsed}
                patientAdminMode={patientAdminMode}
                setPatientAdminMode={setPatientAdminMode}
                patientEditId={patientEditId}
                setPatientEditId={setPatientEditId}
              />
            ) : activeSection === 'ChartReview' ? (
              <ReviewQueuePage />
            ) : activeSection === 'Billing' ? (
              <BillingHome
                activeSubsection={activeSubsection}
                setActiveSubsection={setActiveSubsection}
                sidebarCollapsed={sidebarCollapsed}
              />
            ) : (
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
                <p>Select a menu item from the sidebar to view content.</p>
              </div>
            )}
          </main>
      </div>
      {showClinicianNav && (
        <ClinicianBottomNav
          active={activeSection === 'MyDay' ? 'MyDay' : 'Patients'}
          onSelect={(tab) => {
            if (tab === 'Menu') { setSidebarCollapsed(false); return; }
            setActiveSection(tab === 'MyDay' ? 'MyDay' : 'Patients');
            setActiveSubsection(null);
          }}
        />
      )}
    </div>
    </NotificationProvider>
  );
};

export default Dashboard;
